import { Plus } from "lucide-react";
import { useState } from "react";
import styles from "./home-faq-section.module.css";

type FaqBlock = string | { items: readonly string[] };

const faqs = [
  {
    question: "What is Boardblend?",
    answer: [
      "Boardblend is a shared canvas where your team can talk through a problem, explore ideas with AI, and shape the strongest thinking into one clear visual plan. Teammates can work privately, add comments and context, and suggest changes for the session leader to review.",
    ],
  },
  {
    question: "How does AI-powered brainstorming work?",
    answer: [
      "Each participant can connect a compatible AI assistant to the session. It can use the live transcript, uploaded documents, comments, and canvas to answer questions and suggest new ideas.",
      "Your conversations with your AI stay private unless you choose to share something with the team. Any suggested canvas changes are reviewed by the session leader before they are added.",
    ],
  },
  {
    question: "How is Boardblend different from ChatGPT, Miro, or FigJam?",
    answer: [
      "ChatGPT is mainly designed for conversations with one AI. Miro and FigJam give teams a shared visual workspace.",
      "Boardblend brings both together. Every teammate can explore the same problem privately with their own AI, then contribute their strongest ideas to one shared canvas. Suggestions remain attributed, and the session leader controls what becomes part of the final plan.",
    ],
  },
  {
    question: "Can my whole team join without paying?",
    answer: [
      "Yes. Each brainstorm supports up to 10 participants, and participants can join unlimited sessions for free.",
      "You only need Pro to create and keep multiple brainstorms—not to collaborate in someone else’s.",
    ],
  },
  {
    question: "What is included in the Free plan?",
    answer: [
      "The Free plan gives you one brainstorm of your own at a time, with all the core features included:",
      {
        items: [
          "Up to 10 participants",
          "Personal AI connections",
          "Live transcription",
          "Document uploads",
          "Shared visual canvas",
          "Comments and collaboration",
          "Complete history for that brainstorm",
        ],
      },
      "You can also join unlimited brainstorms created by other people.",
    ],
  },
  {
    question: "Does everyone need their own AI agent?",
    answer: [
      "No. Participants do not need to connect an AI.",
      "Participants can join the session, follow the canvas, add comments, upload context, and contribute ideas without one. Those who connect an AI get a private space to explore the session before deciding what to share.",
    ],
  },
  {
    question: "Can Boardblend transcribe meetings and use uploaded documents?",
    answer: [
      "Yes. In desktop Chrome or Edge, the session leader can capture their microphone and audio from a selected meeting tab to create a live transcript.",
      "You can also upload PDFs, images, text files, Markdown, Word documents, and PowerPoint presentations as shared context for the team and its AI assistants.",
    ],
  },
  {
    question: "Can Boardblend be used for remote and in-person sessions?",
    answer: [
      "Yes. For remote meetings, the session leader can capture both their microphone and audio from the meeting tab.",
      "For in-person sessions, Boardblend can use microphone-only transcription while everyone follows and contributes through the same shared room.",
    ],
  },
  {
    question: "What happens to my brainstorms if I cancel Pro?",
    answer: [
      "You keep Pro until the end of your billing period. After that, your existing brainstorms remain available with their history and core features intact.",
      "You will not be able to create another brainstorm while you are above the Free plan’s one-brainstorm limit. You can delete or repurpose an existing one, or subscribe again.",
    ],
  },
] satisfies ReadonlyArray<{ answer: readonly FaqBlock[]; question: string }>;

export function HomeFaqSection() {
  const [openQuestion, setOpenQuestion] = useState<string | null>(null);

  return (
    <section
      className={styles.section}
      id="faq"
      aria-labelledby="home-faq-heading"
    >
      <div className={styles.heading}>
        <p>Frequently asked questions</p>
        <h2 id="home-faq-heading">A few things worth knowing.</h2>
      </div>

      <div className={styles.list}>
        {faqs.map(({ answer, question }, index) => {
          const isOpen = openQuestion === question;
          const answerId = `faq-answer-${index}`;

          return (
            <article className={styles.item} key={question}>
              <h3>
                <button
                  type="button"
                  aria-controls={answerId}
                  aria-expanded={isOpen}
                  onClick={() => setOpenQuestion(isOpen ? null : question)}
                >
                  <span>{question}</span>
                  <Plus aria-hidden="true" />
                </button>
              </h3>
              <div
                className={styles.answer}
                data-open={isOpen}
                id={answerId}
              >
                <div>
                  {answer.map((block, blockIndex) =>
                    typeof block === "string" ? (
                      <p key={block}>{block}</p>
                    ) : (
                      <ul key={blockIndex}>
                        {block.items.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    ),
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map(({ answer, question }) => ({
              "@type": "Question",
              acceptedAnswer: {
                "@type": "Answer",
                text: answer
                  .flatMap((block) =>
                    typeof block === "string" ? block : block.items,
                  )
                  .join(" "),
              },
              name: question,
            })),
          }).replace(/</g, "\\u003c"),
        }}
      />
    </section>
  );
}
